import { LogOut } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { flushSync } from 'react-dom'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'

import { useCatalog } from '@/catalog/CatalogProvider'
import { CommandPalette } from '@/components/CommandPalette'
import { PageBoundary } from '@/components/feedback/SectionBoundary'
import { NAV, visibleItems } from '@/components/layout/nav'
import { logout, type Me } from '@/lib/auth'
import { confirmLeave } from '@/lib/leaveGuard'
import { fetchOps } from '@/lib/ops'

export default function AppShell({ me }: { me: Me }) {
  const display = me.name ?? me.email
  const initial = display.trim().charAt(0).toUpperCase() || '?'
  const navigate = useNavigate()

  // Route view-transition: the app uses <BrowserRouter> (not a data router), so RR's
  // `viewTransition` prop is inert. Drive it ourselves — wrap the navigation in
  // document.startViewTransition + flushSync so the browser snapshots the outgoing page
  // before React swaps the outlet. Modifier-clicks (new tab) and unsupported browsers fall
  // through to the NavLink's default navigation.
  function onNavClick(e: React.MouseEvent, to: string) {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
    if (typeof document.startViewTransition !== 'function') return
    if (to === location.pathname) return
    e.preventDefault()
    document.startViewTransition(() => {
      flushSync(() => navigate(to))
    })
  }

  const [paletteOpen, setPaletteOpen] = useState(false)

  // Global Cmd+K / Ctrl+K listener
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setPaletteOpen((v) => !v)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Live counts, derived so they never drift as operations/catalog grow. Endpoints removed
  // from the Atlassian spec stay listed in Operations → All Atlassian API but are not counted.
  const catalog = useCatalog()
  const explorerCount = useMemo(
    () =>
      Array.isArray(catalog.products)
        ? catalog.products.reduce(
          (sum, p) => sum + (Array.isArray(p.categories)
            ? p.categories.reduce((s, c) => s + (Array.isArray(c.operations) ? c.operations.filter((o) => !o.removed_at).length : 0), 0)
            : 0),
          0,
        )
        : 0,
    [catalog],
  )
  const [liveCount, setLiveCount] = useState<number | null>(null)
  useEffect(() => {
    fetchOps()
      .then((ops) => setLiveCount(Array.isArray(ops) ? ops.length : null))
      .catch(() => {})
  }, [])

  return (
    <div className="flex h-screen overflow-hidden bg-background text-foreground">
      <nav
        aria-label="Main"
        className="flex w-[190px] shrink-0 flex-col overflow-y-auto border-r border-line bg-sidebar px-2.5 pb-3 pt-3.5"
      >
        <div className="flex items-baseline justify-between px-2 pb-1">
          <span className="font-mono text-[13px] font-medium">
            vulpio<span className="text-primary">/</span><span className="text-text-2">admin</span>
          </span>
          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            aria-label="Open command palette"
            title="Search pages, operations, users (⌘K)"
            className="rounded border border-line-strong px-1.5 font-mono text-[11px] leading-[18px] text-text-3 transition-colors hover:text-foreground"
          >
            ⌘K
          </button>
        </div>

        {NAV.map((g) => {
          const items = visibleItems(g.items, me)
          if (items.length === 0) return null
          const headingId = `nav-group-${g.group.toLowerCase()}`
          return (
            <div key={g.group}>
              <div
                id={headingId}
                className="mx-2 mb-1 mt-3.5 text-[11px] font-medium uppercase tracking-[0.08em] text-text-3"
              >
                {g.group}
              </div>
              <ul aria-labelledby={headingId} className="space-y-px">
                {items.map((l) => {
                  const Icon = l.icon
                  return (
                    <li key={l.to}>
                      <NavLink
                        to={l.to}
                        onClick={(e) => onNavClick(e, l.to)}
                        className={({ isActive }) =>
                          [
                            'relative flex items-center gap-2 rounded-[5px] px-2 py-[5px] text-[13px] transition-colors',
                            isActive
                              ? 'bg-surface-active text-foreground before:absolute before:inset-y-0 before:left-0 before:w-0.5 before:rounded-l-[5px] before:bg-primary'
                              : 'text-text-2 hover:bg-surface-raised hover:text-foreground',
                          ].join(' ')
                        }
                      >
                        <Icon className="size-3.5 shrink-0" aria-hidden />
                        <span className="truncate">{l.label}</span>
                      </NavLink>
                    </li>
                  )
                })}
              </ul>
            </div>
          )
        })}

        <div className="mt-auto px-2 pt-5">
          <div className="mb-3 font-mono text-[11px] leading-relaxed text-text-3">
            {liveCount ?? '—'} live ops
            <br />
            {explorerCount.toLocaleString()} Atlassian endpoints
          </div>
          <div className="border-t border-line pt-3">
            <div className="flex items-center gap-2">
              <span
                className="flex size-7 shrink-0 items-center justify-center rounded-md bg-surface-active font-mono text-[12px] font-medium text-text-2"
                aria-hidden
              >
                {initial}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 text-[12.5px] font-medium text-foreground">
                  <span className="truncate" title={display}>{display}</span>
                  {me.role && (
                    <span className="shrink-0 rounded border border-line-strong px-1 font-mono text-[11px] leading-4 text-text-2">
                      {me.role}
                    </span>
                  )}
                </div>
                <div className="truncate font-mono text-[11px] text-text-3" title={me.email}>
                  {me.email}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                if (confirmLeave()) void logout()
              }}
              className="mt-2 flex w-full items-center gap-1.5 rounded-[5px] px-2 py-1 text-[12px] text-text-2 transition-colors hover:bg-surface-raised hover:text-foreground"
            >
              <LogOut className="size-3.5" aria-hidden />
              Sign out
            </button>
          </div>
        </div>
      </nav>
      <div className="relative min-w-0 flex-1 overflow-auto">
        <PageBoundary>
          <Outlet />
        </PageBoundary>
      </div>

      {/* Command palette — mounted once, globally toggled via Cmd/Ctrl+K */}
      <CommandPalette me={me} open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  )
}
